import { Entity } from '@toggle/toggle';
import React, { ReactElement } from 'react';

import { ChatInputProps } from '~/components/chat';
import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useCompanyCompetitors } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { NewsView } from '~/views/news/components/news-view/NewsView';
import { AssetFinancialsWidget } from '~/widgets/asset-financials-widget/AssetFinancialsWidget';
import { ChartPeerGroup } from '~/widgets/chart-peer-group/ChartPeerGroup';
import { Document } from '~/widgets/document-widget/DocumentWidget';
import { Earnings } from '~/widgets/earnings/Earnings';
import { KnowledgeGraphWidget } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { Overview } from '~/widgets/overview/Overview';
import { PEER_GROUP_DISPLAYED } from '~/widgets/overview/utils';

import { TabsType } from './types';

export interface AssetOverviewTabsProps {
  entity: Entity;
  activeTab: TabsType;
  renderChatInput?: (props?: ChatInputProps) => ReactElement;
}

export const TabContent = ({
  entity,
  activeTab,
  renderChatInput,
}: AssetOverviewTabsProps) => {
  const { competitorsData, isFetching } = useCompanyCompetitors({
    entityTag: entity.tag,
    limit: PEER_GROUP_DISPLAYED,
  });
  const peerEntities = competitorsData.map(s => s.entity);

  const renderElement = () => {
    switch (activeTab) {
      case TabsType.Financials: {
        return <AssetFinancialsWidget entity={entity} />;
      }

      case TabsType.Chart: {
        return (
          <ChartPeerGroup
            entity={entity}
            peerEntities={peerEntities}
            isFetching={isFetching}
          />
        );
      }

      case TabsType.KnowledgeGraph: {
        return <KnowledgeGraphWidget entity={entity} />;
      }

      case TabsType.NewsInsights: {
        return <NewsView entity={entity} includedFilterKeys={['providers']} />;
      }

      case TabsType.Documents: {
        return (
          <ErrorBoundaryWrapper showBorder={false}>
            <Document
              entityTag={entity.tag}
              renderChatInput={renderChatInput}
            />
          </ErrorBoundaryWrapper>
        );
      }

      case TabsType.Earnings: {
        return <Earnings entity={entity} renderChatInput={renderChatInput} />;
      }

      case TabsType.Overview:

      default: {
        return <Overview entity={entity} key={entity.tag} />;
      }
    }
  };

  return <>{renderElement()}</>;
};
